import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getServicesByShopId, Service, getAvailableSlots, AvailableSlot, getAvailableTimes, AvailableTime, getShopById, Shop, bookAppointment, getUserBookings, Booking } from '../supabase/queries';
import { supabase } from '../supabase/client';
import { CalendarDays, Clock, MapPin, DollarSign, Scissors } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const BookAppointmentPage: React.FC = () => {
  const { shopId } = useParams<{ shopId: string }>();
  const navigate = useNavigate();

  // Common state for both booking form and booking list
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null); // Added for user's name

  // State for booking form (if shopId is present)
  const [services, setServices] = useState<Service[]>([]);
  const [shop, setShop] = useState<Shop | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [availableDates, setAvailableDates] = useState<AvailableSlot[]>([]);
  const [selectedDate, setSelectedDate] = useState<AvailableSlot | null>(null);
  const [availableTimes, setAvailableTimes] = useState<AvailableTime[]>([]);
  const [selectedTime, setSelectedTime] = useState<AvailableTime | null>(null);
  const [bookingLoading, setBookingLoading] = useState(false);

  // State for booking list (if shopId is NOT present)
  const [userBookings, setUserBookings] = useState<Booking[]>([]);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const location = useLocation();
  const [highlightedBookingId, setHighlightedBookingId] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        setError('User not logged in.');
        setLoading(false);
        navigate('/login');
        return null;
      }
      setCustomerId(user.id);
      setUserName(user.user_metadata?.full_name || user.email); // Set user name
      return user.id;
    };

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      const currentCustomerId = await fetchUser();
      if (!currentCustomerId) return;

      if (shopId) {
        // Logic for booking a new appointment
        try {
          const shopData = await getShopById(shopId);
          setShop(shopData);
          const servicesData = await getServicesByShopId(shopId);
          setServices(servicesData || []);
        } catch (err) {
          console.error('Error fetching initial data for booking form:', err);
          setError('Failed to load initial data for booking.');
        } finally {
          setLoading(false);
        }
      } else {
        // Logic for displaying user's bookings
        try {
          const bookingsData = await getUserBookings(currentCustomerId);
          setUserBookings(bookingsData || []);
          // Check for highlighted booking from navigation state
          if (location.state?.highlightedBookingId) {
            setHighlightedBookingId(location.state.highlightedBookingId);
            // Clear the state after reading it to prevent re-highlighting on subsequent visits
            navigate(location.pathname, { replace: true, state: {} });
          }
        } catch (err) {
          console.error('Error fetching user bookings:', err);
          setError('Failed to load your bookings.');
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [shopId, navigate, location.state]);

  useEffect(() => {
    if (highlightedBookingId) {
      const timer = setTimeout(() => {
        setHighlightedBookingId(null);
      }, 5000); // Highlight for 5 seconds
      return () => clearTimeout(timer);
    }
  }, [highlightedBookingId]);

  useEffect(() => {
    const fetchAvailableDates = async () => {
      if (error) return;
      if (selectedService && shopId) {
        const datesData = await getAvailableSlots(shopId, selectedService.id);
        setAvailableDates(datesData || []);
        setSelectedDate(null);
        setAvailableTimes([]);
        setSelectedTime(null);
      }
    };
    if (shopId) { // Only fetch if in booking form mode
      fetchAvailableDates();
    }
  }, [selectedService, shopId, error]);

  useEffect(() => {
    const fetchAvailableTimes = async () => {
      if (error) return;
      if (selectedService && selectedDate && shopId) {
        const timesData = await getAvailableTimes(shopId, selectedService.id, selectedDate.date);
        setAvailableTimes(timesData || []);
        setSelectedTime(null);
      }
    };
    if (shopId) { // Only fetch if in booking form mode
      fetchAvailableTimes();
    }
  }, [selectedService, selectedDate, shopId, error]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center text-white">
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center text-red-500">
        Error: {error}
      </div>
    );
  }

  const renderBookingForm = () => (
    <>
      {/* Header for Booking Form */}
      <div className="flex items-center justify-between p-4 bg-gray-800 shadow-md">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-700 transition">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-white">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h17" />
          </svg>
        </button>
        <h1 className="text-lg font-bold">Book Appointment with {shop?.name || 'ER'}</h1>
        <button onClick={() => navigate('/')} className="p-2 rounded-full hover:bg-gray-700 transition">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-white">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Main Content for Booking Form */}
      <div className="flex-grow p-4 overflow-y-auto">
        {/* Service Selection */}
        <h2 className="text-xl font-bold mb-3">Service</h2>
        <div className="flex overflow-x-auto space-x-3 pb-2 mb-5">
          {services.map((service) => (
            <div
              key={service.id}
              className={`flex-none w-36 p-3 rounded-lg border-2 cursor-pointer ${
                selectedService?.id === service.id ? 'border-teal-500 bg-teal-900' : 'border-gray-700 bg-gray-800 hover:border-gray-600'
              }`}
              onClick={() => setSelectedService(service)}
            >
              <h3 className="text-base font-semibold">{service.name}</h3>
              <p className="text-gray-400 text-xs">{service.duration_value} {service.duration_type}</p>
              <p className="text-teal-400 font-bold text-base">${service.price}</p>
            </div>
          ))}
        </div>

        {/* Date Selection */}
        <h2 className="text-xl font-bold mb-3">Date</h2>
        <div className="flex overflow-x-auto space-x-3 pb-2 mb-5">
          {availableDates.length === 0 ? (
            <p className="text-gray-400 flex-none text-sm">No dates available for this service.</p>
          ) : (
            availableDates.map((dateSlot) => (
              <div
                key={dateSlot.date}
                className={`flex-none w-20 p-2 rounded-lg border-2 text-center ${
                  dateSlot.isClosed
                    ? 'border-gray-800 bg-gray-900 text-gray-600 cursor-not-allowed'
                    : selectedDate?.date === dateSlot.date
                    ? 'border-teal-500 bg-teal-900 cursor-pointer'
                    : 'border-gray-700 bg-gray-800 hover:border-gray-600 cursor-pointer'
                }`}
                onClick={() => !dateSlot.isClosed && setSelectedDate(dateSlot)}
              >
                <p className="text-xs">{dateSlot.dayOfWeek}</p>
                <p className="text-lg font-bold">{new Date(dateSlot.date).getDate()}</p>
                <p className="text-xs">
                  {dateSlot.isClosed ? 'Closed' : `${dateSlot.slotsAvailable} Slots`}
                </p>
              </div>
            ))
          )}
        </div>

        {/* Time Selection */}
        <h2 className="text-xl font-bold mb-3">Time</h2>
        <div className="grid grid-cols-3 gap-2 mb-5">
          {selectedDate && availableTimes.length === 0 ? (
            <p className="text-gray-400 col-span-3 text-sm">No times available for this date.</p>
          ) : (
            availableTimes.map((timeSlot) => (
              <div
                key={timeSlot.formattedTime}
                className={`p-2 rounded-lg border-2 text-center ${
                  timeSlot.isBooked
                    ? 'border-gray-800 bg-gray-900 text-gray-600 cursor-not-allowed'
                    : selectedTime?.formattedTime === timeSlot.formattedTime
                    ? 'border-teal-500 bg-teal-900 cursor-pointer'
                    : 'border-gray-700 bg-gray-800 hover:border-gray-600 cursor-pointer'
                }`}
                onClick={() => !timeSlot.isBooked && setSelectedTime(timeSlot)}
              >
                <p className="text-base font-bold">{timeSlot.formattedTime}</p>
              </div>
            ))
          )}
        </div>

        {/* Booking Summary */}
        <h2 className="text-xl font-bold mb-3 text-teal-400">Booking Summary</h2>
        <div className="bg-gray-800 p-4 rounded-lg mb-6 border border-teal-500">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <Scissors className="w-4 h-4 text-gray-400 mr-2" />
              <p className="text-gray-400 text-sm">Service</p>
            </div>
            <p className="text-white text-sm">{selectedService?.name || '---'}</p>
          </div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <CalendarDays className="w-4 h-4 text-gray-400 mr-2" />
              <p className="text-gray-400 text-sm">Date</p>
            </div>
            <p className="text-white text-sm">{selectedDate ? new Date(selectedDate.date).toLocaleDateString() : '---'}</p>
          </div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Clock className="w-4 h-4 text-gray-400 mr-2" />
              <p className="text-gray-400 text-sm">Time</p>
            </div>
            <p className="text-white text-sm">{selectedTime?.formattedTime || '---'}</p>
          </div>
          <div className="flex items-center justify-between pt-4 border-t border-gray-700">
            <div className="flex items-center">
              <DollarSign className="w-4 h-4 text-yellow-400 mr-2" />
              <p className="text-yellow-400 font-bold text-base">Price</p>
            </div>
            <p className="text-teal-400 font-bold text-base">${selectedService?.price || '0'}</p>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="sticky bottom-0 left-0 right-0 bg-gray-900 p-4 shadow-lg z-30">
        <button
          className={`w-full py-2 text-lg font-bold rounded-lg transition ${
            selectedService && selectedDate && selectedTime && customerId && !bookingLoading
              ? 'bg-teal-600 hover:bg-teal-700 text-white'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
          disabled={!selectedService || !selectedDate || !selectedTime || !customerId || bookingLoading}
          onClick={async () => {
            if (selectedService && selectedDate && selectedTime && customerId && shopId) {
              setBookingLoading(true);
              setError(null);
              try {
                // Combine date and time into a TIMESTAMPTZ string
                const startTime = `${selectedDate.date}T${selectedTime.rawStartTime}Z`;

                const bookingId = await bookAppointment(
                  customerId,
                  shopId,
                  selectedService.id,
                  selectedTime.providerId,
                  startTime
                );

                if (bookingId) {
                  navigate('/bookings', { state: { highlightedBookingId: bookingId } });
                } else {
                  setError('Failed to book appointment. Please try again.');
                }
              } catch (err) {
                console.error('Error during booking:', err);
                setError('An unexpected error occurred during booking.');
              } finally {
                setBookingLoading(false);
              }
            }
          }}
        >
          {bookingLoading ? 'Booking...' : 'Book Appointment'}
        </button>
      </div>
    </>
  );

  const renderBookingList = () => {
    const now = new Date();
    const upcomingBookings = userBookings.filter(booking => new Date(booking.start_time) >= now);
    const pastBookings = userBookings.filter(booking => new Date(booking.start_time) < now);

    const bookingsToDisplay = activeTab === 'upcoming' ? upcomingBookings : pastBookings;

    return (
      <div className="flex flex-col h-full">
        {/* Top Header */}
        <div className="p-4 bg-gray-900 text-white flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-400">Welcome back,</p>
            <h2 className="text-xl font-bold">{userName || 'Guest'}</h2>
          </div>
          <div className="flex items-center space-x-4">
            <button onClick={() => supabase.auth.signOut()} className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded-md text-sm font-medium">
              Logout
            </button>
            <button className="relative p-2 rounded-full hover:bg-gray-700 transition">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-white">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.248 24.248 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
              </svg>
              <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-500"></span>
            </button>
          </div>
        </div>

        {/* Your Appointments Section */}
        <div className="p-4 bg-gray-900 text-white">
          <h1 className="text-2xl font-bold">Your Appointments</h1>
          <p className="text-gray-400 text-sm">Manage your schedule and history.</p>
        </div>

        {/* Tabs for Upcoming/Completed Bookings */}
        <div className="flex justify-around bg-gray-900 p-2 mb-4 rounded-lg mx-4 mt-4">
          <button
            className={`flex-1 py-2 text-center rounded-md transition-colors ${
              activeTab === 'upcoming' ? 'bg-teal-600 text-white' : 'text-gray-400 hover:bg-gray-700'
            }`}
            onClick={() => setActiveTab('upcoming')}
          >
            Upcoming ({upcomingBookings.length})
          </button>
          <button
            className={`flex-1 py-2 text-center rounded-md transition-colors ${
              activeTab === 'past' ? 'bg-teal-600 text-white' : 'text-gray-400 hover:bg-gray-700'
            }`}
            onClick={() => setActiveTab('past')}
          >
            Completed ({pastBookings.length})
          </button>
        </div>

        {/* Booking List */}
        <div className="flex-grow p-4 overflow-y-auto">
          {bookingsToDisplay.length === 0 ? (
            <p className="text-gray-400 text-center mt-8">No {activeTab} bookings found.</p>
          ) : (
            <div className="space-y-4">
              {bookingsToDisplay.map((booking) => {
                const shopNameInitial = booking.shops?.name ? booking.shops.name.substring(0, 2).toUpperCase() : '??';
                const isUpcoming = new Date(booking.start_time) >= now;
                const isHighlighted = highlightedBookingId === booking.id;

                return (
                  <div
                    key={booking.id}
                    className={`bg-gray-800 p-4 rounded-lg border shadow-md transition-all duration-500 ease-in-out ${
                      isHighlighted ? 'border-yellow-400 ring-4 ring-yellow-400 ring-opacity-50' : 'border-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        {/* Shop Initial Circle */}
                        <div className="w-12 h-12 rounded-full bg-teal-600 flex items-center justify-center text-white text-lg font-bold mr-3">
                          {shopNameInitial}
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">{booking.shops?.name || 'Unknown Shop'}</h3>
                          <p className="text-gray-400 text-sm">{booking.services?.name || 'Unknown Service'}</p>
                        </div>
                      </div>
                      {/* Status Tag */}
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isUpcoming ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-200'}`}>
                        {isUpcoming ? 'Upcoming' : 'Completed'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-y-2 text-sm text-gray-300 mb-4">
                      <p className="flex items-center"><CalendarDays className="w-4 h-4 mr-2 text-teal-400" /> {new Date(booking.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                      <p className="flex items-center"><Clock className="w-4 h-4 mr-2 text-teal-400" /> {new Date(booking.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</p>
                      <p className="flex items-center"><MapPin className="w-4 h-4 mr-2 text-teal-400" /> {booking.shops?.address || 'N/A'}</p>
                      <p className="flex items-center"><DollarSign className="w-4 h-4 mr-2 text-teal-400" /> ${booking.services?.price?.toFixed(2) || '0.00'}</p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-4 border-t border-gray-700 pt-3">
                      {isUpcoming ? (
                        <>
                          <button className="flex items-center text-red-500 hover:text-red-400 transition text-sm font-medium">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.924a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m-1.022.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.924a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165M12 4.556v1.5m0 9V12" />
                            </svg>
                            Cancel
                          </button>
                          <button className="flex items-center text-teal-400 hover:text-teal-300 transition text-sm font-medium">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.106l-1.412-.353a1.125 1.125 0 01-.924-.924L19.5 7.75a2.25 2.25 0 00-2.25-2.25h-1.372c-.516 0-.966.351-1.106.852l-.353 1.412a1.125 1.125 0 01-.924.924L10.5 19.5a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 01-2.25-2.25V6.75z" />
                            </svg>
                            Contact
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="flex items-center text-teal-400 hover:text-teal-300 transition text-sm font-medium">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.181m0-7.963l3.181-3.181m0 0H9.542m5.975 9.243a3.375 3.375 0 10-7.5 0m7.5 0l-.414 1.176M12 10.5h.002M4.75 19.5h14.5c1.1 0 2-.9 2-2V6.5c0-1.1-.9-2-2-2H4.75c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2z" />
                            </svg>
                            Rebook
                          </button>
                          <button className="flex items-center text-yellow-400 hover:text-yellow-300 transition text-sm font-medium">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.123 5.116 5.432.547a.562.562 0 01.32 1.005l-4.165 3.606 1.277 5.271a.562.562 0 01-.84 0l-4.737-2.815-4.737 2.815a.562.562 0 01-.84 0l1.277-5.271-4.165-3.606a.562.562 0 01.32-1.005l5.432-.547L11.48 3.499z" />
                            </svg>
                            Review
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {shopId ? renderBookingForm() : renderBookingList()}
    </div>
  );
};

export default BookAppointmentPage;
